/**
 * Typed wrappers around `/api/me/companies/:companyId/payroll/pila/*` (PF-485).
 * Mirrors apps/api/src/routes/payroll-pila.ts and the shapes built by
 * apps/api/src/lib/payroll/pila/pila-planillas.ts (pilaPreflight,
 * planillaRowToSummary, planillaRowToDetail).
 *
 * The SERVER builds the planilla from the confirmed liquidations, validates it
 * at PagoSimple and decides when it is paid. The client only renders what it
 * answers — never recompute contributions here.
 */
import { fetchApiBlobWithAuth, fetchApiWithAuth } from "@/lib/api";
import { defaultPayrollMonthStart } from "@/lib/payroll-period";

export type PilaPlanillaStatus =
  | "validating"
  | "validated"
  | "awaiting_payment"
  | "paid"
  | "rejected"
  | "expired"
  | "superseded";

export type AdministradoraType = "EPS" | "AFP" | "ARL" | "CCF";
export type Administradora = {
  code: string;
  name: string;
  source: "sandbox" | "portal" | "ministerio";
};
export type PilaCatalog = Record<AdministradoraType, Administradora[]>;

export type PilaSettings = {
  arlCode: string | null;
  arlActivityCode: string | null;
  ccfCode: string | null;
  linked: boolean;
  linkedAt: string | null;
  contributorId: string | null;
};

export type UpdatePilaSettingsInput = {
  arlCode?: string | null;
  arlActivityCode?: string | null;
  ccfCode?: string | null;
};

export type PilaEmployeeAffiliations = {
  employeeId: string;
  epsCode: string | null;
  afpCode: string | null;
  ccfCode: string | null;
};

export type UpdatePilaAffiliationsInput = {
  epsCode?: string | null;
  afpCode?: string | null;
  ccfCode?: string | null;
};

export type PilaIssueCode =
  | "EMPLOYEE_DOCUMENT_MISSING"
  | "EMPLOYEE_EPS_MISSING"
  | "EMPLOYEE_AFP_MISSING"
  | "EMPLOYEE_CCF_MISSING"
  | "EMPLOYEE_LOCATION_MISSING"
  | "EMPLOYEE_WORKER_TYPE_UNSUPPORTED"
  | "EMPLOYEE_NOVELTY_DATES_MISSING"
  | "EMPLOYEE_HIGH_RISK_AFP_NOT_COLPENSIONES"
  | "EMPLOYEE_HIGH_RISK_LEAVE_FUNDING_REQUIRED"
  | "EMPLOYEE_FSP_OVER_DEDUCTED";

export type PilaIssue = {
  code: PilaIssueCode;
  employeeId: string;
  field?: string;
  /** The liquidation to correct (missing novelty dates). */
  liquidationId?: string;
};

export type PilaBlocker =
  | { code: "NO_CONFIRMED_LIQUIDATIONS" }
  | { code: "DRAFT_LIQUIDATIONS"; liquidationIds: string[] }
  | {
      code:
        | "ARL_CODE_MISSING"
        | "ARL_ACTIVITY_MISSING"
        | "COMPANY_DOCUMENT_MISSING"
        | "NOT_LINKED";
    }
  | (PilaIssue & { employeeName: string });

export type PilaTotals = {
  contributors: number;
  ibc: number;
  pension: number;
  fsp: number;
  health: number;
  arl: number;
  ccf: number;
  sena: number;
  icbf: number;
  total: number;
};

export type PilaPreflight = {
  period: string;
  healthPeriod: string;
  settings: PilaSettings;
  employees: Array<{
    employeeId: string;
    name: string;
    liquidationIds: string[];
    issues: PilaIssue[];
    /** FSP payroll withheld, what the planilla files and what was already corrected. */
    fsp?: {
      deducted: number;
      filed: number;
      adjusted: number;
      overWithheld: number;
    };
  }>;
  draftLiquidations: Array<{
    id: string;
    employeeId: string;
    employeeName: string;
  }>;
  withoutLiquidation: Array<{ employeeId: string; name: string }>;
  blockers: PilaBlocker[];
  estimate: PilaTotals | null;
  livePlanilla: { id: string; status: PilaPlanillaStatus } | null;
  /** FSP corrections recorded for the month: owed back to employees (2505). */
  fspAdjustments?: PilaFspAdjustment[];
};

export type PilaFspAdjustment = {
  id: string;
  employeeId: string;
  period: string;
  liquidationIds: string[];
  deducted: number;
  filed: number;
  amount: number;
  journalEntryId: string;
  createdByUserId: string;
  createdAt: string;
};

export type PilaPlanillaSummary = {
  id: string;
  period: string;
  healthPeriod: string;
  status: PilaPlanillaStatus;
  payrollNumber: string | null;
  contributors: number;
  estimatedTotal: number | null;
  totalToPay: number | null;
  limitDate: string | null;
  providerStatus: string | null;
  paidConfirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** One type 2 record: the worked days, an incapacity (IGE) or an unpaid leave (SLN). */
export type PilaRecord = {
  kind: "base" | "ige" | "sln";
  startDate: string | null;
  endDate: string | null;
  days: number;
  ibc: number;
  pension: number;
  fspSolidarity: number;
  fspSubsistence: number;
  health: number;
  arl: number;
  ccf: number;
  sena: number;
  icbf: number;
  total: number;
};

export type PilaContributor = {
  employeeId: string;
  sequence: number;
  name: string;
  documentType: string;
  document: string;
  days: number;
  ibc: number;
  exonerated: boolean;
  arlClass: number;
  pension: number;
  fspSolidarity: number;
  fspSubsistence: number;
  health: number;
  arl: number;
  ccf: number;
  sena: number;
  icbf: number;
  total: number;
  novelties: { ing: string | null; ret: string | null; vst: boolean };
  highRiskPension?: boolean;
  /** Absent on planillas generated before novelty records existed. */
  records?: PilaRecord[];
  epsCode: string;
  afpCode: string;
  ccfCode: string;
};

export type PilaAdministratorTotal = {
  administratorCode: string;
  administratorName: string;
  administratorType: string;
  affiliates: number;
  totalWithoutArrear: number;
  arrearValue: number;
  total: number;
};

export type PilaValidationIssue = {
  description: string;
  field: string | null;
  row: number | null;
  employeeName: string | null;
  autocorrect: boolean;
};

export type PilaPlanillaDetail = PilaPlanillaSummary & {
  payrollCode: string | null;
  arrearValue: number;
  providerCheckedAt: string | null;
  contributorsDetail: PilaContributor[];
  estimatedTotals: PilaTotals | null;
  liabilities: {
    health: number;
    pension: number;
    arl: number;
    parafiscales: number;
  } | null;
  administrators: PilaAdministratorTotal[];
  validation: {
    status: string;
    errors: PilaValidationIssue[];
    warnings: PilaValidationIssue[];
  } | null;
  providerError: {
    providerError: string;
    kind: string;
    retryable: boolean;
  } | null;
  bankAccountId: string | null;
  journalEntryId: string | null;
  receiptDocumentId: string | null;
  supersededById: string | null;
  paymentUrlFresh: boolean;
  paidStatusConfigured: boolean;
  actions: {
    pay: boolean;
    refresh: boolean;
    replace: boolean;
    receipt: boolean;
  };
};

export type PilaRefreshOutcome = "paid" | "pending" | "expired" | "unchanged";

/** Details of PILA_BANK_ACCOUNT_REQUIRED: the accounts the user picks from. */
export type PilaBankChoice = { id: string; name: string };

const base = (companyId: string) =>
  `/api/me/companies/${encodeURIComponent(companyId)}/payroll/pila`;

export function getPilaAvailability(
  companyId: string,
): Promise<{ enabled: boolean }> {
  return fetchApiWithAuth(`${base(companyId)}/availability`);
}

export function getPilaCatalog(companyId: string): Promise<PilaCatalog> {
  return fetchApiWithAuth(`${base(companyId)}/catalog`);
}

export function getPilaSettings(companyId: string): Promise<PilaSettings> {
  return fetchApiWithAuth(`${base(companyId)}/settings`);
}

export function updatePilaSettings(
  companyId: string,
  input: UpdatePilaSettingsInput,
): Promise<PilaSettings> {
  return fetchApiWithAuth(`${base(companyId)}/settings`, {
    method: "PUT",
    body: input,
  });
}

export function linkPagoSimple(companyId: string): Promise<PilaSettings> {
  return fetchApiWithAuth(`${base(companyId)}/link`, { method: "POST" });
}

export function getPilaAffiliations(
  companyId: string,
  employeeId: string,
): Promise<PilaEmployeeAffiliations> {
  return fetchApiWithAuth(
    `${base(companyId)}/employees/${encodeURIComponent(employeeId)}/affiliations`,
  );
}

export function updatePilaAffiliations(
  companyId: string,
  employeeId: string,
  input: UpdatePilaAffiliationsInput,
): Promise<PilaEmployeeAffiliations> {
  return fetchApiWithAuth(
    `${base(companyId)}/employees/${encodeURIComponent(employeeId)}/affiliations`,
    { method: "PUT", body: input },
  );
}

export function getPilaPreflight(
  companyId: string,
  period: string,
): Promise<PilaPreflight> {
  return fetchApiWithAuth(
    `${base(companyId)}/preflight?period=${encodeURIComponent(period)}`,
  );
}

/** Move FSP withheld beyond the planilla to an amount owed to the employee (once per month). */
export function recordPilaFspAdjustment(
  companyId: string,
  input: { period: string; employeeId: string },
): Promise<{ adjustment: PilaFspAdjustment; created: boolean }> {
  return fetchApiWithAuth(`${base(companyId)}/fsp-adjustments`, {
    method: "POST",
    body: input,
  });
}

export function listPilaPlanillas(
  companyId: string,
): Promise<{ planillas: PilaPlanillaSummary[] }> {
  return fetchApiWithAuth(`${base(companyId)}`);
}

export function generatePilaPlanilla(
  companyId: string,
  period: string,
): Promise<PilaPlanillaDetail> {
  return fetchApiWithAuth(`${base(companyId)}`, {
    method: "POST",
    body: { period },
  });
}

export function getPilaPlanilla(
  companyId: string,
  planillaId: string,
): Promise<PilaPlanillaDetail> {
  return fetchApiWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}`,
  );
}

export function replacePilaPlanilla(
  companyId: string,
  planillaId: string,
): Promise<PilaPlanillaDetail> {
  return fetchApiWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}/replace`,
    {
      method: "POST",
    },
  );
}

export function requestPilaPaymentUrl(
  companyId: string,
  planillaId: string,
  bankAccountId?: string | null,
): Promise<{ url: string; planilla: PilaPlanillaDetail }> {
  return fetchApiWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}/payment-url`,
    { method: "POST", body: bankAccountId ? { bankAccountId } : {} },
  );
}

export function refreshPilaStatus(
  companyId: string,
  planillaId: string,
): Promise<{ outcome: PilaRefreshOutcome; planilla: PilaPlanillaDetail }> {
  return fetchApiWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}/refresh-status`,
    { method: "POST" },
  );
}

export function downloadPilaFile(
  companyId: string,
  planillaId: string,
): Promise<Blob> {
  return fetchApiBlobWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}/file`,
  );
}

export function downloadPilaReceipt(
  companyId: string,
  planillaId: string,
): Promise<Blob> {
  return fetchApiBlobWithAuth(
    `${base(companyId)}/${encodeURIComponent(planillaId)}/receipt`,
  );
}

/**
 * Months offered for a planilla, newest first, starting with the one before the
 * current month in Bogotá (the month usually paid: mes vencido).
 */
export function pilaPeriods(count = 13, now = new Date()): string[] {
  const [year, month] = defaultPayrollMonthStart(now)
    .split("-")
    .map(Number) as [number, number];
  return Array.from({ length: count }, (_, index) => {
    const total = year * 12 + (month - 1) - 1 - index;
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
  });
}

/** "septiembre de 2026" / "September 2026" for a YYYY-MM period. */
export function formatPilaPeriod(period: string, locale: string): string {
  const [year, month] = period.split("-").map(Number) as [number, number];
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 15)));
}
